import routesLimited, { hasProperty } from '@/constants/routesLimited'
import { routes as initRoutes, router } from '@/plugins/router/index'
import { useHasProperty } from 'composables/hasProperty.ts'
import storeConfigs from 'constants/storeConfigs.ts'
import { sortBy } from 'lodash'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
	type RouteLocationNormalizedLoaded,
	type RouteMeta,
	type RouteRecord,
	type RouteRecordRaw,
} from 'vue-router'
import { useUserStore } from './user'
import { useClientStore } from './client'

type Item = {
	title: string
	props: {
		prependIcon?: string
		to?: { name: string }
		disabled: boolean
		onClick?: () => {}
	}
	children?: Item[]
	meta?: RouteMeta
}

const isNavRoute = (route: RouteRecord) => route.meta?.nav === true

export const useNavStore = defineStore(storeConfigs.nav.name, () => {
	const status = ref<Api.Status>('initial')

	const flat = ref<any[] | undefined>(undefined)
	const init = ref<any[] | undefined>(undefined)
	const routes = ref<any[] | undefined>(undefined)

	const routeHistory = ref([])
	const maxHistorySize = 10

	const user = useUserStore()
	const client = useClientStore()

	const nav = computed(() =>
		routes.value
			? sortBy(filterRoutesBy(routes.value, isNavRoute), 'meta.ordering')
			: []
	)

	function mapRouteToListItem(
		routes: RouteRecordRaw[],
		transform: (item: RouteRecordRaw, hasChildren: boolean) => Item
	) {
		const mapRoute = (item: RouteRecordRaw) => {
			const hasChildren =
				Array.isArray(item.children) && item.children.length > 0

			const mapped: Item = transform(item, hasChildren)

			if (item.children && item.children.length > 0) {
				mapped.children = mapRouteToListItem(item.children, transform)
			}

			return mapped
		}

		return routes.map(mapRoute)
	}

	function getPreviousRoute(index = 2) {
		if (routeHistory.value.length - index >= 1) {
			return routeHistory.value[routeHistory.value.length - index]
		} else {
			return null
		}
	}

	const filterRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
		const result: RouteRecordRaw[] = []

		const processRoutes = (routes: RouteRecordRaw[], isFirstLevel = false) => {
			routes = routes || []
			routes.forEach((route) => {
				if (isFirstLevel) {
					// On the first level, only include routes with offline: true
					if (route.meta?.offline === true) {
						result.push(route)
					}
				} else {
					// For child routes, include if offline is true or undefined
					if (
						route.meta?.offline === true ||
						route.meta?.offline === undefined
					) {
						result.push(route)
					}
				}

				if (
					route.children &&
					((isFirstLevel && route.meta?.offline === true) || !isFirstLevel)
				) {
					const filteredChildren = route.children.filter(
						(child) => child.meta?.offline !== false
					)
					processRoutes(filteredChildren, false)
				}
			})
		}

		processRoutes(routes, true)
		return result
	}

	const findRoute = (routes, targetName, recursive = true) => {
		for (const route of routes) {
			if (route.name === targetName || route.meta?.name === targetName) {
				return route
			}
			if (route.children && recursive) {
				const found = findRoute(route.children, targetName)
				if (found) return found
			}
		}
		return null
	}

	function addRouteToHistory(route: RouteLocationNormalizedLoaded) {
		if (
			(routeHistory.value.length > 0 &&
				routeHistory.value[routeHistory.value.length - 1].fullPath ===
					route.fullPath) ||
			route.fullPath.indexOf('redirect') !== -1 ||
			route.fullPath.indexOf('error') !== -1
		) {
			return
		}
		routeHistory.value.push(route)

		if (routeHistory.value.length > maxHistorySize) {
			routeHistory.value.shift()
		}
	}
	function clearRouteHistory() {
		routeHistory.value = []
	}
	function removeRouteFromHistory() {
		routeHistory.value.pop()
	}
	function flatSet({ routes }: { routes: RouteRecordRaw[] }) {
		flat.value = routes
	}
	function initSet({ routes }: { routes: RouteRecordRaw[] }) {
		init.value = routes
	}
	function routesSet({ passedRoutes }: { passedRoutes: RouteRecordRaw[] }) {
		routes.value = passedRoutes
	}

	function filterRoutesBy(
		routes: RouteRecord[],
		by: (route: RouteRecord) => boolean
	): RouteRecord[] {
		return (
			routes
				.map((route) => {
					const isVisible = by(route)
					if (!isVisible) return null

					const filteredChildren = route.children
						? filterRoutesBy(route.children, by)
						: undefined

					if (isVisible || (filteredChildren && filteredChildren.length > 0)) {
						return {
							...route,
							children: filteredChildren,
						}
					}
					return null
				})
				// null-Werte entfernen
				.filter((route): route is RouteRecord => route !== null)
		)
	}

	function hasAccess(route: RouteRecord) {
		let hidden = route.meta?.hidden ?? []
		let limited = route.meta?.limited ?? []

		hidden = Array.isArray(hidden) ? hidden : [hidden]
		limited = Array.isArray(limited) ? limited : [limited]

		const accesGranted = (items: any[]) =>
			items.some((item) => validateLimited(item) === true)

		if (hidden.length > 0 && !accesGranted(hidden)) return false

		if (limited.length > 0 && !accesGranted(limited)) return false

		return true
	}

	function validateLimited(limit) {
		const { isAdmin } = user
		if (typeof limit === 'object') {
			if (
				!Object.prototype.hasOwnProperty.call(limit, 'method') ||
				!Object.prototype.hasOwnProperty.call(limit, 'parameter')
			) {
				console.error(
					`Limit option must have method and parameter property for limited`,
					limit
				)
			}
			switch (limit.method) {
				case hasProperty:
					return useHasProperty(limit.parameter).value
				default:
					console.error(`Undefined value for limited/method`, limit)
			}
		} else {
			switch (limit) {
				case routesLimited.is_admin:
					return isAdmin
				case routesLimited.is_team:
					return user.isTeam
				case routesLimited.can_settings:
					return user.canSettings
				case routesLimited.can_post:
					return user.canPost
				case routesLimited.can_update:
					return user.canUpdate
				case routesLimited.can_delete:
					return user.canDelete
				case routesLimited.isAdminOrTeam:
					return isAdmin || user.isTeam
				case routesLimited.is_karthago:
					return client.isKarthago
				case routesLimited.is_sync_failures:
					// Sync-Fehler-Feature nur für ERP mit Failure-Tracking-Sync
					// (HausPerfekt Postgres + Karthago), nicht hpGen/hpXpa.
					return (
						(isAdmin || user.isTeam) && (client.isKarthago || client.isPsql)
					)
				case routesLimited.is_access_rejections:
					// Verwalter-Endpunkte (AAR-1847) hängen an admin-team-permission,
					// keine ERP-Einschränkung (beide Sync-Arten betroffen).
					return isAdmin || user.isTeam
				default:
					throw new Error(`Undefined value for limited: ${limit}`)
			}
		}
	}

	async function initialise({
		routes,
		onlyAuthenticated,
	}: {
		routes: RouteRecordRaw[]
		onlyAuthenticated?: boolean
	}) {
		const isAuthRoute = (route: RouteRecord) => route.meta?.auth === true

		const items = routes.filter((route) =>
			onlyAuthenticated ? isAuthRoute(route) : true
		)
		const value = filterRoutesBy(items, hasAccess)

		routesSet({ passedRoutes: value })
		flat.value = initRoutes
		init.value = routes

		router.clearRoutes()
		value.forEach((route) => router.addRoute(route))
	}

	function $reset() {
		status.value = 'initial'
		flat.value = undefined
		init.value = undefined
		routeHistory.value = []
		routes.value = undefined
	}

	return {
		flat,
		init,
		nav,
		routeHistory,
		routes,
		status,
		router,
		addRouteToHistory,
		getPreviousRoute,
		filterRoutes,
		findRoute,
		clearRouteHistory,
		removeRouteFromHistory,
		flatSet,
		initSet,
		routesSet,
		initialise,
		mapRouteToListItem,
		$reset,
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useNavStore, import.meta.hot))
}
